import type { Permission } from '@/lib/rbac/permissions';
import { COLLECTIONS } from './collections';
import { LEAD_TYPES, isUnreadKind, type UnreadKind } from './leadTypes';

/**
 * Information architecture (spec §7). Each node is gated by the permission that reveals it
 * (hide if unreadable). The sidebar renders from this + RBAC (plan §3.3 / §4.3).
 */
export interface NavLeaf {
  type: 'leaf';
  navKey: string; // i18n key under nav.*
  to: string;
  permission: Permission;
  /** B-R16-07: the list's key in the unopened counts, when it carries a badge. */
  unread?: UnreadKind;
}
export interface NavGroup {
  type: 'group';
  navKey: string;
  children: NavLeaf[];
}
export type NavNode = NavGroup | NavLeaf;

export const NAV: NavNode[] = [
  /*
   * The teacher's whole menu (round 10, phase 26; D-201). `teach:own` is held
   * by `teacher` and by no other role — not even `admin`, who works groups
   * through the cohort screens — so these two leaves are the menu a teacher
   * sees and nobody else does. Account is listed for them because it is the
   * one other screen they have: the account menu above holds it for everyone.
   */
  { type: 'leaf', navKey: 'myGroups', to: '/teach', permission: 'teach:own' },
  { type: 'leaf', navKey: 'account', to: '/account', permission: 'teach:own' },
  { type: 'leaf', navKey: 'dashboard', to: '/', permission: 'content:read' },
  {
    type: 'group',
    navKey: 'content',
    children: [
      // Pages — the copy on the site's own pages (D1) — plus the two V1 collections.
      // Product families and the eight legacy collections are dropped (D4).
      //
      // This points at `/content/pages`, the page-COPY console, not at `/pages`, the
      // agency page-structure builder. Editing a page's structure is not a thing this
      // deployment offers: the site's pages are React routes, and the only page-level
      // thing an editor changes is the wording. The builder's routes stay registered
      // (a `pages` record is still what the usages panel deep-links into) but nothing
      // navigates to them.
      { type: 'leaf', navKey: 'pages', to: '/content/pages', permission: 'content:read' },
      // A collection replaced by another (round 9: teachers and team members → people)
      // leaves the menu; its list stays reachable by address and from its notice.
      ...COLLECTIONS.filter((c) => !c.replacedBy).map(
        (c): NavLeaf => ({
          type: 'leaf',
          navKey: c.navKey,
          to: `/content/${c.key}`,
          permission: 'content:read',
        }),
      ),
    ],
  },
  { type: 'leaf', navKey: 'media', to: '/media', permission: 'content:read' },
  {
    type: 'group',
    navKey: 'leads',
    children: [
      ...LEAD_TYPES.map(
        (t): NavLeaf => ({
          type: 'leaf',
          navKey: t.navKey,
          to: `/leads/${t.key}`,
          // Per TYPE, not per group: the seminar applications are gated on
          // `students:read` (D-S3), so an editor sees the Leads group without the
          // one item in it they may not open. See config/leadTypes.ts.
          permission: t.readPermission,
          unread: isUnreadKind(t.key) ? t.key : undefined,
        }),
      ),
      /* Round 10, phase 25: the register-interest sign-ups (invited to a cohort
         when it opens) and the Calendly bookings nobody could match. Both name
         people who may be minors, so both are on the student read gate. */
      { type: 'leaf', navKey: 'interestLeads', to: '/leads/interest', permission: 'students:read', unread: 'interest' },
      { type: 'leaf', navKey: 'calendlyBookings', to: '/leads/calendly', permission: 'students:read', unread: 'calendly' },
    ],
  },
  /*
   * Programme sits AFTER Leads because that is the direction the work runs: a
   * seminar application is promoted into a student record, and that record is
   * enrolled on a cohort. It became a group when PHASE 09 added cohorts beside
   * students — before that it was a leaf, because a disclosure triangle over a
   * single item is a gesture the reader has to make for nothing.
   *
   * Both children carry `students:read` rather than the group doing it: the
   * roster of a cohort names minors, so it is admin-only on exactly the same
   * terms as the records themselves (D-S3).
   */
  {
    type: 'group',
    navKey: 'programme',
    children: [
      /* Round 10, phase 24: everyone waiting on something, across every cohort —
         first, because it is where a working day starts. */
      { type: 'leaf', navKey: 'needsAction', to: '/needs-action', permission: 'students:read' },
      { type: 'leaf', navKey: 'students', to: '/students', permission: 'students:read' },
      /* The possible-duplicates queue (§8.11), beside the records it merges. */
      { type: 'leaf', navKey: 'duplicates', to: '/duplicates', permission: 'students:read' },
      { type: 'leaf', navKey: 'cohorts', to: '/cohorts', permission: 'students:read' },
      /* Consented alumni records (round 9, PHASE 13). On the student read gate
         because they ARE student data, and they outlive the student record once
         retention erases it, so they need a list of their own rather than only
         a tab on a record that may no longer exist. Delete is gated on
         `students:delete` at the control. */
      { type: 'leaf', navKey: 'alumni', to: '/alumni', permission: 'students:read' },
      /* Campaigns is a sibling of the records it mails, on the same
         `students:read` gate: the audience is a student filter and the preview
         names students, so anybody who can open the screen can already read the
         records it draws from. Writing and sending are gated separately, at the
         controls. */
      { type: 'leaf', navKey: 'campaigns', to: '/campaigns', permission: 'students:read' },
      /* Round 10, phase 25: every message sent to a student, across the
         installation. Composing is not a menu item: every send starts from the
         people it is for (a profile, a group, a Needs-action reason). */
      { type: 'leaf', navKey: 'emailLog', to: '/emails', permission: 'students:read' },
      /* The organisation's own numbers (§8.10): admin-only, never on the site. */
      { type: 'leaf', navKey: 'numbers', to: '/numbers', permission: 'students:read' },
      /* Round 10, phase 26: the public numbers, current next to calculated,
         and the switch between them. Settings permission, because switching
         one changes what the site says. */
      { type: 'leaf', navKey: 'publicNumbers', to: '/numbers/public', permission: 'settings:manage' },
    ],
  },
  { type: 'leaf', navKey: 'audit', to: '/audit', permission: 'audit:read' },
  {
    type: 'group',
    navKey: 'administration',
    children: [
      { type: 'leaf', navKey: 'users', to: '/users', permission: 'users:manage' },
      // Machine credentials for the LLM connector / CI. Same permission as Users:
      // minting a token is granting write access, so it belongs to whoever can
      // already create accounts.
      { type: 'leaf', navKey: 'apiTokens', to: '/tokens', permission: 'users:manage' },
      // Every OAuth grant and every registered AI app. Same permission as API
      // tokens: a grant lets an app act as a person, so it belongs to whoever
      // already administers people.
      { type: 'leaf', navKey: 'aiConnections', to: '/ai-connections', permission: 'users:manage' },
      { type: 'leaf', navKey: 'settings', to: '/settings', permission: 'settings:manage' },
    ],
  },
];
